/* ==========================================================================
   upgrade-calc.js -- the simple instalment calculator on "How to pay"
   (sections/upgrade-finance-page.liquid)

   Omar, 26 September 2026: "i just want a simpler installement calculator
   nothing too complex and not with the providers".

   The whole calculation: price / months, rounded UP to the whole pound so the
   figure can never understate what someone pays. No fee, no interest, no
   provider -- the page says "before any fees" beside the number, because
   Upgrade's instalment terms are not on file and inventing them would be an
   offer this site cannot back.

   Every word comes from the section's settings (data-* attributes and the
   markup), so the Arabic storefront translates them in the admin. Digits
   typed in Arabic-Indic form (٣٠٠٠٠) are read the same as 30000.

   ARABIC PHASE 4, 4 Oct 2026 (LOG-043; 2026-10-04_arabic/phase4-home-shop.md).
   Still no word in this file. Two additions, both inert on English:
     - the detail template may carry [term]: it is filled with the chosen
       chip's own label ("3 شهور", the Arabic plural the section prints
       from upgrade.finance.months), because Arabic changes the noun with
       the number and "[months] months" cannot say that. English's template
       has no [term], so the value is never used there;
     - data-announce-template (printed by the section on a translated page
       only) is the screen-reader sentence as one template; without it the
       sentence is joined exactly as before.
   ========================================================================== */
(function () {
  'use strict';

  var AR_DIGITS = '٠١٢٣٤٥٦٧٨٩';
  var FA_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

  /* "30,000" / "30 000" / "٣٠٬٠٠٠" / "30000.5" -> a number, or null.
     Only a single decimal point is honoured; everything else that is not a
     digit is a separator and is dropped. */
  function toNumber(raw) {
    if (raw === null || raw === undefined) return null;
    var s = String(raw).replace(/[٠-٩۰-۹]/g, function (d) {
      var i = AR_DIGITS.indexOf(d);
      return String(i >= 0 ? i : FA_DIGITS.indexOf(d));
    });
    s = s.replace(/٫/g, '.');            // Arabic decimal separator
    s = s.replace(/[^0-9.]/g, '');
    var firstDot = s.indexOf('.');
    if (firstDot !== -1) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
    }
    if (!s || s === '.') return null;
    var n = parseFloat(s);
    return isFinite(n) && n > 0 ? n : null;
  }

  function group(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, function (whole, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : whole;
    });
  }

  function init(root) {
    if (root.getAttribute('data-ug-calc-ready') === '1') return;
    root.setAttribute('data-ug-calc-ready', '1');

    var input = root.querySelector('[data-ug-calc-price]');
    var radios = root.querySelectorAll('[data-ug-calc-months]');
    var outMonthly = root.querySelector('[data-ug-calc-monthly]');
    var outMeta = root.querySelector('[data-ug-calc-meta]');
    var status = root.querySelector('[data-ug-calc-status]');
    if (!input || !radios.length || !outMonthly || !outMeta) return;

    var template = root.getAttribute('data-meta-template') || '';
    var emptyText = root.getAttribute('data-empty-text') || '';
    var resultLabel = root.getAttribute('data-result-label') || '';
    var unit = root.getAttribute('data-unit') || 'EGP';
    var announceTemplate = root.getAttribute('data-announce-template') || '';
    var timer = null;
    var primed = false;   // the first render (URL prefill) is shown, not spoken

    function months() {
      for (var i = 0; i < radios.length; i++) {
        if (radios[i].checked) return parseInt(radios[i].value, 10) || null;
      }
      return null;
    }

    // The chosen chip's own label, for a template's [term] (Arabic phase 4).
    function term() {
      for (var i = 0; i < radios.length; i++) {
        if (!radios[i].checked) continue;
        var chip = radios[i].closest('.ug-calc__chip');
        return chip ? String(chip.textContent || '').replace(/\s+/g, ' ').trim() : '';
      }
      return '';
    }

    function syncChips() {
      for (var i = 0; i < radios.length; i++) {
        var chip = radios[i].closest('.ug-calc__chip');
        if (chip) chip.classList.toggle('is-selected', radios[i].checked);
      }
    }

    function announce(message) {
      if (!status) return;
      clearTimeout(timer);
      if (!primed) return;
      timer = setTimeout(function () { status.textContent = message; }, 700);
    }

    function render() {
      var price = toNumber(input.value);
      var m = months();
      syncChips();
      if (!price || !m) {
        outMonthly.textContent = '—';
        outMeta.textContent = emptyText;
        announce('');
        return;
      }
      var monthly = Math.ceil(price / m);
      var detail = fill(template, { months: String(m), total: group(Math.round(price)), term: term() });
      outMonthly.textContent = group(monthly);
      outMeta.textContent = detail;
      announce(announceTemplate
        ? fill(announceTemplate, { label: resultLabel, monthly: group(monthly), unit: unit, detail: detail })
        : resultLabel + ' ' + group(monthly) + ' ' + unit + ', ' + detail + '.');
    }

    input.addEventListener('input', render);
    input.addEventListener('blur', function () {
      var price = toNumber(input.value);
      if (price) input.value = group(Math.round(price));
    });
    for (var i = 0; i < radios.length; i++) radios[i].addEventListener('change', render);

    // ?price=30000 fills the calculator, so a product page can link to it.
    try {
      var fromUrl = toNumber(new URLSearchParams(window.location.search).get('price'));
      if (fromUrl) input.value = group(Math.round(fromUrl));
    } catch (e) { /* no URLSearchParams: the field just starts empty */ }

    render();
    primed = true;
  }

  function boot() {
    var roots = document.querySelectorAll('[data-ug-calc]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', boot);
})();
